import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'store/store';
import {
  selectMembers,
  selectBuildingUnits,
  selectBuildingUnitTenancies,
  selectTenancyLeases,
  selectTenancyCois,
  selectTenancyFloorplans,
  selectTenancyInsuranceRequirementsDocuments,
  selectBillingContacts,
  selectAccessPaths
} from 'store/Tenancy/selectors';
import { selectCleaningServiceSchedules } from 'store/CleaningService/selectors';
import { selectProductSubscriptions } from 'store/Consumables/selectors';
import { selectTenancyServices } from 'store/FurnitureAndEquipment/selectors';
import { selectCustomerSupportTickets } from 'store/AIAssistant/selectors';
import { selectIsTenancyOnboarded } from 'store/v2/User/selectors';
import { TenantOnboardingChecklistItem } from 'ux/Tenant/TenantAdmin/requests';

import { deriveChecklistStates, hasDefaultChecklistItems } from './deriveChecklistStates';

export const selectOnboardingChecklistItems = createSelector(
  [
    selectMembers,
    selectBuildingUnits,
    selectBuildingUnitTenancies,
    selectTenancyLeases,
    selectTenancyCois,
    selectTenancyFloorplans,
    selectTenancyInsuranceRequirementsDocuments,
    selectBillingContacts,
    selectCleaningServiceSchedules,
    selectProductSubscriptions,
    selectTenancyServices,
    selectCustomerSupportTickets,
    (_state: RootState, skippedChecklistItems?: TenantOnboardingChecklistItem[]) => skippedChecklistItems,
    selectAccessPaths
  ],
  (
    members,
    buildingUnits,
    buildingUnitTenancies,
    tenancyLeases,
    tenancyCois,
    tenancyFloorplans,
    tenancyInsuranceRequirementsDocuments,
    billingContacts,
    cleaningServiceSchedules,
    consumablesProductSubscriptions,
    tenancyServices,
    supportTickets,
    skippedChecklistItems,
    accessPaths
  ) => {
    return deriveChecklistStates({
      members: members || [],
      buildingUnits: buildingUnits || [],
      buildingUnitTenancies: buildingUnitTenancies || [],
      tenancyLeases,
      tenancyCois,
      tenancyFloorplans,
      tenancyInsuranceRequirementsDocuments,
      billingContacts,
      cleaningServiceSchedules: cleaningServiceSchedules || [],
      consumablesProductSubscriptions,
      tenancyServices,
      supportTickets: supportTickets || [],
      skippedChecklistItems: skippedChecklistItems || [],
      accessPaths
    });
  }
);

export const selectTenantOnboardingChecklist = createSelector(
  [
    (state: RootState, skippedChecklistItems?: TenantOnboardingChecklistItem[]) =>
      selectOnboardingChecklistItems(state, skippedChecklistItems)
  ],
  checklistData => checklistData
);

export const showTenantOnboardingChecklist = createSelector(
  [
    selectIsTenancyOnboarded,
    (state: RootState, skippedChecklistItems?: TenantOnboardingChecklistItem[]) =>
      selectOnboardingChecklistItems(state, skippedChecklistItems)
  ],
  (isOnboarded, checklistData) => {
    if (isOnboarded) {
      return false;
    }

    return hasDefaultChecklistItems(checklistData);
  }
);
